// Lightweight reactivity system for state management

export function reactive(obj) {
    const handlers = new Map(); // key -> Set<callback>
    const proxy = new Proxy(obj, {
        get(target, key, receiver) {
            const value = Reflect.get(target, key, receiver);
            // Track dependency if inside a computed
            if (currentComputed) {
                if (!handlers.has(key)) handlers.set(key, new Set());
                handlers.get(key).add(currentComputed);
            }
            return value;
        },
        set(target, key, value, receiver) {
            const result = Reflect.set(target, key, value, receiver);
            // Notify subscribers
            if (handlers.has(key)) {
                for (const fn of handlers.get(key)) {
                    fn();
                }
            }
            return result;
        },
    });
    return proxy;
}

let currentComputed = null;

export function computed(fn) {
    let value;
    let dirty = true;
    const deps = new Set();

    const getter = () => {
        if (dirty) {
            currentComputed = getter;
            deps.clear();
            try {
                value = fn();
            } finally {
                currentComputed = null;
            }
            dirty = false;
        }
        return value;
    };

    // Mark dirty when dependencies change
    const markDirty = () => {
        if (!dirty) {
            dirty = true;
            // Re-evaluate on next access
        }
    };

    // Subscribe to dependencies when first accessed
    const proxy = {
        get value() {
            return getter();
        },
    };

    // Allow manual subscription for effect-like behavior
    proxy.subscribe = (callback) => {
        const wrapper = () => {
            dirty = true;
            callback(getter());
        };
        // Trigger on any dependency change
        return wrapper;
    };

    return proxy;
}

export function effect(fn) {
    const cleanup = () => {};
    const run = () => {
        currentComputed = run;
        try {
            fn();
        } finally {
            currentComputed = null;
        }
    };
    run();
    return cleanup;
}

export function watch(source, callback) {
    let oldValue = source.value;
    const stop = effect(() => {
        const newValue = source.value;
        if (newValue !== oldValue) {
            callback(newValue, oldValue);
            oldValue = newValue;
        }
    });
    return stop;
}
